import { useCallback, useEffect, useState } from 'react';
import './AdminInsightsCard.css';

function formatSyncStatus(hasEverSyncedFiles) {
  return hasEverSyncedFiles ? 'Synced' : 'Never synced';
}

export function AdminInsightsCard({ app, manager }) {
  const [insights, setInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const appId = app?.id || app?._id;

  const loadInsights = useCallback(async () => {
    if (!appId || !manager?.fetchAdminAppInsights) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setErrorMessage('');
    try {
      const data = await manager.fetchAdminAppInsights(appId);
      setInsights(data);
    } catch (error) {
      setInsights(null);
      setErrorMessage(error?.message || 'Failed to load admin insights');
    } finally {
      setIsLoading(false);
    }
  }, [appId, manager]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  if (!app) return null;

  const appTypeNorm = String(app.appType || '').toLowerCase().trim();
  const isLocalApp = appTypeNorm === 'local';

  return (
    <div className="account-card settings-card admin-insights-card">
      <div className="card-header">
        <h2 className="card-title">
          <i className="fa-solid fa-shield-halved" aria-hidden /> Admin insights
        </h2>
      </div>
      <div className="card-body admin-insights-body">
        {isLoading && (
          <div className="admin-insights-status">
            <i className="fa-solid fa-spinner fa-spin" aria-hidden /> Loading insights...
          </div>
        )}

        {!isLoading && errorMessage && (
          <div className="admin-insights-error" role="alert">
            <i className="fa-solid fa-circle-exclamation" aria-hidden /> {errorMessage}
          </div>
        )}

        {!isLoading && !errorMessage && insights && (
          <div className="admin-insights-grid">
            <div className="admin-insights-row">
              <span className="admin-insights-label">Files ever synced</span>
              <span className="admin-insights-value">
                {formatSyncStatus(insights.hasEverSyncedFiles)}
              </span>
            </div>
            <div className="admin-insights-row">
              <span className="admin-insights-label">Non-owner synced</span>
              <span className="admin-insights-value">
                {insights.hasNonOwnerSyncedFiles ? 'Yes' : 'No'}
              </span>
            </div>
            {isLocalApp && !insights.hasEverSyncedFiles && (
              <p className="admin-insights-note">
                Local apps use on-disk storage; cloud sync flags usually apply to browser apps only.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
